import { Group, Text } from '@mantine/core';
import { 
  IconFileTypePdf, 
  IconFileTypeDoc, 
  IconFileTypeXls, 
  IconFileTypePpt,
  IconFileTypePng,
  IconFileTypeJpg,
  IconFile,
  IconTrash
} from '@tabler/icons-react';
import { AfaAnchor } from '../AfaAnchor';
import { AfaMenu, type AfaMenuItemProps } from '../AfaMenu';
import { useNavigate } from 'react-router-dom';

export interface AfaFileItem {
  name: string;
  type?: 'pdf' | 'doc' | 'xls' | 'ppt' | 'png' | 'jpg' | 'other';
  url?: string;
  id?: string;
  created?: any;
}

export interface AfaFileListProps {
  /**
   * Lista de archivos a mostrar
   */
  files: AfaFileItem[];
  /**
   * Variante del componente
   * - link: Muestra solo el nombre como enlace clicable (AfaAnchor)
   * - readonly: Muestra el nombre como texto + AfaMenu variant="alert" con opciones
   * @default 'link'
   */
  variant?: 'link' | 'readonly';
  /**
   * Callback cuando se elimina un archivo
   * Si se proporciona, automáticamente se agrega la opción "Eliminar" al menú (variant="readonly")
   */
  onDelete?: (file: AfaFileItem, index: number) => void;
  /**
   * Items personalizados del menú (solo para variant="readonly")
   * La opción "Eliminar" se agrega automáticamente al final si onDelete está definido
   */
  menuItems?: Array<Omit<AfaMenuItemProps, 'onClick'> & {
    onClick?: (file: AfaFileItem, index: number) => void;
  }>;
  onClick?: any;
}

const AfaFileList = ({ 
  files, 
  variant = 'link',
  onDelete,
  menuItems,
  onClick
}: AfaFileListProps) => {
  
  // Función para obtener el icono según el tipo de archivo
  const getFileIcon = (type?: string) => {
    const iconSize = 20;
    const iconColor = 'var(--mantine-color-red-6)';
    
    switch (type) {
      case 'pdf':
        return <IconFileTypePdf size={iconSize} color={iconColor} />;
      case 'doc':
        return <IconFileTypeDoc size={iconSize} color="var(--mantine-color-blue-6)" />;
      case 'xls':
        return <IconFileTypeXls size={iconSize} color="var(--mantine-color-green-6)" />;
      case 'ppt':
        return <IconFileTypePpt size={iconSize} color="var(--mantine-color-orange-6)" />;
      case 'png':
        return <IconFileTypePng size={iconSize} color="var(--mantine-color-cyan-6)" />;
      case 'jpg':
        return <IconFileTypeJpg size={iconSize} color="var(--mantine-color-cyan-6)" />;
      default:
        return <IconFile size={iconSize} color="var(--mantine-color-gray-6)" />;
    }
  };

  // Preparar items del menú con las funciones que reciben file e index
  const prepareMenuItems = (file: AfaFileItem, index: number): AfaMenuItemProps[] => {
    const items: AfaMenuItemProps[] = [];
    
    // Agregar items personalizados del menú
    if (menuItems) {
      menuItems.forEach(item => {
        const menuItem: AfaMenuItemProps = {
          label: item.label,
        };
        
        if (item.icon !== undefined) menuItem.icon = item.icon;
        if (item.color !== undefined) menuItem.color = item.color;
        if (item.disabled !== undefined) menuItem.disabled = item.disabled;
        if (item.divider !== undefined) menuItem.divider = item.divider;
        if (item.onClick) {
          menuItem.onClick = () => item.onClick!(file, index);
        }
        
        items.push(menuItem);
      });
    }
    
    // Siempre agregar opción de eliminar al final si onDelete está definido
    if (onDelete) {
      // Agregar divider si hay items antes
      if (items.length > 0) {
        items.push({ label: '', divider: true });
      }
      
      items.push({
        label: 'Eliminar',
        icon: <IconTrash size={14} />,
        color: 'red',
        onClick: () => onDelete(file, index)
      });
    }
    
    return items;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {files.map((file, index) => (
        <Group
          key={file.id || index}
          gap="sm"
          style={{
            padding: '8px 12px',
            border: '1px solid var(--mantine-color-gray-3)',
            borderRadius: '8px',
            backgroundColor: 'var(--mantine-color-gray-0)',
          }}
        >
          {/* Icono del tipo de archivo */}
          {getFileIcon(file.type)}

          {/* Variante Link - Nombre como enlace */}
          {variant === 'link' && (
            <>
              <AfaAnchor
                href={file.url || '#'}
                size="md"
                style={{ flex: 1 }}
                onClick={onClick}
              >
                {file.name}
              </AfaAnchor>
              {file.created && (
                <Text size="sm">
                  {new Date(file.created).toLocaleDateString('ca-AD', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  })}
                </Text>
              )}
            </>
          )}

          {/* Variante Readonly - Nombre como texto + Menu */}
          {variant === 'readonly' && (
            <>
              <Text size="md" fw={400} style={{ flex: 1 }}>
                {file.name}
              </Text>
              
              <AfaMenu
                variant="alert"
                items={prepareMenuItems(file, index)}
                triggerProps={{
                  onClick: onDelete ? () => onDelete(file, index) : undefined
                }}
              />
            </>
          )}
        </Group>
      ))}
    </div>
  );
};

export default AfaFileList;
